import { AxiosError } from "axios";
import {
  DayWithHours,
  Message,
  MessageMenuItem,
  MessageMenuResponse,
  MessagePrev,
} from "../types";
import { baseFetch } from "./base";
import { pingSocketAfterSubmit } from "../utils/socket";
import type { PreassessmentQuestion } from "./quiz";

type LegacyMessageMenuRow = { text: string; description?: string };

function isMessageMenuItem(value: unknown): value is MessageMenuItem {
  if (!value || typeof value !== "object") return false;
  const o = value as Record<string, unknown>;
  return (
    typeof o.id === "string" &&
    (o.kind === "action" || o.kind === "submenu") &&
    typeof o.label === "string"
  );
}

function parseMessageMenuResponse(data: unknown): MessageMenuItem[] {
  if (data == null) return [];

  if (Array.isArray(data)) {
    if (data.length === 0) return [];
    if (isMessageMenuItem(data[0])) {
      return data as MessageMenuItem[];
    }
    return (data as LegacyMessageMenuRow[]).map((row, i) => ({
      id: `legacy.${i}`,
      kind: "action" as const,
      label: row.text,
      meta: row.description ? { description: row.description } : undefined,
    }));
  }

  if (typeof data === "object" && "items" in (data as object)) {
    const items = (data as MessageMenuResponse).items;
    return Array.isArray(items) ? items.filter(isMessageMenuItem) : [];
  }

  return [];
}

export const getMessageMenu = async (
  type: string,
  message_type: string,
  options?: {
    tab_id?: string;
    tab_label?: string;
    menu_preset?: string;
    menu_exclude_ids?: string[];
  },
): Promise<MessageMenuItem[]> => {
  const payload: Record<string, string> = {
    type,
    message_type,
  };
  if (options?.tab_id) payload.tab_id = options.tab_id;
  if (options?.tab_label) payload.tab_label = options.tab_label;
  if (options?.menu_preset) payload.menu_preset = options.menu_preset;
  if (options?.menu_exclude_ids?.length) {
    payload.menu_exclude_ids = JSON.stringify(options.menu_exclude_ids);
  }
  const response = await baseFetch.post<unknown>("/message_menu", payload);
  return parseMessageMenuResponse(response.data);
};

type DaysServer = {
  message_id: string;
  days: DayWithHours[];
};

interface UserMessagePaginationResponse {
  up: boolean;
  down: boolean;
  message_id: string | null;
}

export interface LessonPaginationResponse {
  up: boolean;
  down: boolean;
  message_id: string | null;
}

export interface InlineButton {
  id?: string;
  title: string;
  promt_example?: string;
  subtitle?: string;
  children?: InlineButton[];
}

export interface InlineButtonsData {
  menuTitle: string;
  items: InlineButton[];
}

export const normalizeInlineButtonsResponse = (
  data: unknown,
): InlineButtonsData => {
  if (Array.isArray(data)) {
    return { menuTitle: "", items: data as InlineButton[] };
  }
  if (data && typeof data === "object") {
    const obj = data as Record<string, unknown>;
    const rawItems = obj.items ?? obj.buttons;
    const items = Array.isArray(rawItems) ? (rawItems as InlineButton[]) : [];
    const menuTitle = String(obj.menu_title ?? obj.menuTitle ?? "");
    return { menuTitle, items };
  }
  return { menuTitle: "", items: [] };
};

export interface MessageInteractionStats {
  message_id: string;
  user_id: string;
  action: string;
  duration: number;
}

export interface PreassessmentBypassStatisticPayload {
  domain: string;
  focus: string;
  total_questions: number;
  answered_before_bypass: number;
  scores_earned_before_bypass: number;
  bypassed_questions: PreassessmentQuestion[];
}

export const postCalendarWeekChosen = async (calendarData: DaysServer) => {
  try {
    const response = await baseFetch.post(
      "/calendar-week-chosen",
      calendarData,
    );
    return response.data;
  } catch (error) {
    console.error("Error posting calendar week chosen:", error);
    throw error;
  }
};

export const sendReaction = async (reaction: string, messageId: string) => {
  try {
    await baseFetch.post("/message_reaction", {
      reaction,
      message_id: messageId,
    });
  } catch (error) {
    console.error("Error sending reaction:", error);
  }
};

export const sendMessageMenuPress = async (
  text: string,
  description: string,
  messageId: string,
  chosen_text?: string,
  tab_id?: string,
) => {
  try {
    const body: Record<string, string | undefined> = {
      text,
      description,
      message_id: messageId,
      chosen_text,
      tab_id,
    };
    if (!body.tab_id) delete body.tab_id;
    if (body.chosen_text === undefined) delete body.chosen_text;
    await baseFetch.post("/message_menu_press", body);
    pingSocketAfterSubmit();
  } catch (error) {
    console.error("Error sending message menu press:", error);
  }
};

export const getLessonsList = async (): Promise<MessagePrev[]> => {
  try {
    const response = await baseFetch.get("/lessons_list");
    // console.log("Lessons list response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error fetching lessons list:", error);
    throw error;
  }
};

export const getPaginationMessages = async (payload?: {
  message_id?: string;
  direction?: "middle" | "up" | "down";
  number?: number;
}): Promise<Message[]> => {
  try {
    const response = await baseFetch.get("/pagination", { params: payload });
    // console.log("getPaginationMessages", response.data);
    return response.data;
  } catch (error) {
    console.error("Error fetching pagination messages:", error);
    throw error;
  }
};

export const getMessagesByReactions = async (
  reaction: string,
): Promise<MessagePrev[]> => {
  try {
    const response = await baseFetch.get("/messages_by_reactions", {
      params: { reaction },
    });
    // console.log("getMessagesByReactions", response.data);
    return response.data;
  } catch (error) {
    console.error("Error fetching messages by reactions:", error);
    throw error;
  }
};

export const getMessagesByDate = async (
  date: string,
): Promise<{ message_id: string; pagination: Message[] }> => {
  try {
    const response = await baseFetch.get("/messages_by_date", {
      params: { date },
    });
    // console.log("getMessagesByDate", response.data)
    return response.data;
  } catch (error) {
    console.error("Error fetching messages by date:", error);
    throw error;
  }
};

export const getLessonsByScore = async (
  score: string,
): Promise<MessagePrev[]> => {
  try {
    const response = await baseFetch.get("/lessons_by_score", {
      params: { score },
    });
    // console.log("Lessons by score:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error fetching lessons by score:", error);
    throw error;
  }
};

export const postMessageToFavorite = async (
  messageId: string,
  isSaved: boolean
): Promise<string> => {
  try {
    const response = await baseFetch.post("/message_to_favorite", {
      message_id: messageId,
      isSaved,
    });
    return response.data;
  } catch (error) {
    console.error("Error updating favorite status:", error);
    throw error;
  }
};
export const postMessageToTag = async (
  messageId: string,
  tagText: string[]
): Promise<string> => {
  try {
    const response = await baseFetch.post("/tag_message", {
      message_id: messageId,
      tags_list: tagText,
    });
    return response.data;
  } catch (error) {
    console.error("Error updating favorite status:", error);
    throw error;
  }
};

// Use existing /feedback endpoint to mark message as having feedback sent
export const postMessageFeedbackSent = async (
  messageId: string,
  feedbackSent: boolean,
): Promise<string> => {
  try {
    const sentFeedbackData = {
      message_id: messageId,
      feedback_type: "message",
      reason: "feedback_sent_marker",
      reason_label: feedbackSent ? "Feedback sent" : "Feedback removed",
      rating: feedbackSent ? 1 : 0,
      rating_label: feedbackSent ? "Feedback submitted" : "Feedback cancelled",
    };

    // Use the existing feedback endpoint with a special marker
    const response = await baseFetch.post("/feedback", sentFeedbackData);
    return response.data;
  } catch (error) {
    console.error("Error updating feedback sent status:", error);
    throw error;
  }
};

export const getFavoriteMessages = async (): Promise<MessagePrev[]> => {
  try {
    const response = await baseFetch.get("/favorite_messages");
    // console.log("getFavoriteMessages", response.data);
    return response.data;
  } catch (error) {
    console.error("Error fetching favorite messages:", error);
    throw error;
  }
};
export const getTagMessages = async (): Promise<string[]> => {
  try {
    const response = await baseFetch.get("/tags_list");
    // console.log("getFavoriteMessages", response.data);
    return response.data;
  } catch (error) {
    console.error("Error fetching favorite messages:", error);
    throw error;
  }
};

export const getLessonsByCompetence = async (
  competence: string,
): Promise<MessagePrev[]> => {
  try {
    const response = await baseFetch.get("/lessons_by_competence", {
      params: { competence },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching lessons by competence:", error);
    throw error;
  }
};
export const getMessageByTag = async (tag: string): Promise<MessagePrev[]> => {
  try {
    const response = await baseFetch.get("/tagged_messages", {
      params: { tag },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching lessons by competence:", error);
    throw error;
  }
};

export const searchByText = async (text: string): Promise<MessagePrev[]> => {
  try {
    const response = await baseFetch.get("/search_by_text", {
      params: { text },
    });
    return response.data;
  } catch (error) {
    console.error("Error searching by text:", error);
    throw error;
  }
};

export const messageToAudio = async (
  messageId: string,
  tabId?: string,
): Promise<{
  status: string;
  message_id: string;
  tab_id?: string | null;
}> => {
  try {
    const params: Record<string, string> = { message_id: messageId };
    if (tabId) params.tab_id = tabId;
    const response = await baseFetch.get("/message_to_audio", {
      params,
    });
    return response.data;
  } catch (error) {
    console.error("Error converting message to audio:", error);
    throw error;
  }
};

export const getUserMessagePagination = async (params: {
  user_message_id?: string;
  direction?: "up" | "down";
}): Promise<UserMessagePaginationResponse> => {
  try {
    const response = await baseFetch.get("/user_message_pagination", {
      params,
    });
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      console.error(
        "Axios error fetching user message pagination:",
        error.message,
      );
    } else {
      console.error("Unknown error fetching user message pagination:", error);
    }
    throw error;
  }
};

export const getLessonPagination = async (params: {
  message_id?: string;
  direction?: "up" | "down";
}): Promise<LessonPaginationResponse> => {
  try {
    const response = await baseFetch.get("/lesson_pagination", { params });
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      console.error("Axios error fetching lesson pagination:", error.message);
    } else {
      console.error("Unknown error fetching lesson pagination:", error);
    }
    throw error;
  }
};

export const getInlineButtons = async (): Promise<InlineButtonsData> => {
  try {
    const response = await baseFetch.get("/get_inline_buttons");
    return normalizeInlineButtonsResponse(response.data);
  } catch (error) {
    console.error("Error fetching inline buttons:", error);
    throw error;
  }
};

export const getLessonsToDo = async (): Promise<MessagePrev[]> => {
  try {
    const response = await baseFetch.get("/lessons_to_do");
    return response.data;
  } catch (error) {
    console.error("Error fetching lessons to do:", error);
    throw error;
  }
};

export const getCrashCoursesList = async (): Promise<MessagePrev[]> => {
  try {
    const response = await baseFetch.get("/crash_courses_list");
    return response.data;
  } catch (error) {
    console.error("Error fetching crash courses list:", error);
    throw error;
  }
};

export const sendMessageInteractionStats = async (
  stats: MessageInteractionStats,
): Promise<void> => {
  try {
    await baseFetch.post("/communication_statistic", stats);
  } catch (error) {
    console.error("Error sending message interaction stats:", error);
    throw error;
  }
};

export const sendPreassessmentBypassStatistic = async (
  payload: PreassessmentBypassStatisticPayload,
): Promise<void> => {
  try {
    await baseFetch.post("/communication_statistic", {
      message_id: "preassessment",
      action: "preassessment_bypass",
      duration: 0,
      metadata: {
        domain: payload.domain,
        focus: payload.focus,
        total_questions: payload.total_questions,
        answered_before_bypass: payload.answered_before_bypass,
        scores_earned_before_bypass: payload.scores_earned_before_bypass,
        bypassed_questions_count: payload.bypassed_questions.length,
        score_per_question: 5,
        bypassed_questions: payload.bypassed_questions,
      },
    });
  } catch (error) {
    console.error("Error sending preassessment bypass statistic:", error);
    throw error;
  }
};

export const submitCrashCourseForm = async (formData: {
  topic: string;
  depth: "summary" | "detailed" | "comprehensive";
  format: "text" | "audio" | "text+audio";
}): Promise<void> => {
  try {
    await baseFetch.post("/crash_course_create", formData);
  } catch (error) {
    console.error("Error submitting crash course form:", error);
    throw error;
  }
};
